import DOMPurify from 'dompurify';
import { useCanvasStore } from '../store/canvas-store';
import { screenToWorld } from '../canvas/viewport';
import { shortId } from './dropDetect';
import { hardenCss, scopeCssBlock } from '../store/isolate-styles';
import { ingestImage } from '../assets/ingestImage';
import { blobArrayBuffer } from '../assets/assetStore';
import { ingestLottieJson } from '../lottie/ingest';
import { validateLottie } from '../lottie/validate';

// Re-exported for callers/tests that import CSS hardening from the paste module.
export { hardenCss };

const DEFAULT_W = 400;

/** DOMPurify removes <script>, on* handlers and javascript: URLs by default.
 *  We additionally forbid embedding/navigation tags. Inline styles + <style>
 *  are kept for visual fidelity when pasting from the web/design tools. */
const PURIFY_CONFIG = {
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'base', 'meta', 'link', 'form'],
  ALLOW_DATA_ATTR: true,
};

export function sanitizeHtml(raw: string): string {
  return DOMPurify.sanitize(raw, PURIFY_CONFIG) as unknown as string;
}

/** Collect the text of every <style> block in the raw HTML (DOMPurify strips
 *  these from the body, so we recover them separately to re-scope). */
export function extractCss(raw: string): string {
  try {
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    return Array.from(doc.querySelectorAll('style'))
      .map((s) => s.textContent ?? '')
      .join('\n');
  } catch {
    return '';
  }
}

/** Build frame content from HTML: sanitized body + re-scoped <style> blocks.
 *  CSS recovered from <head>/<body> is wrapped in @scope under .paste-root so
 *  it only styles this frame. (Render-time isolateStyles skips already-scoped
 *  blocks, so this stays the single scoping pass for pasted content.) */
export function buildFromHtml(raw: string): PasteContent {
  const body = sanitizeHtml(raw);
  const css = extractCss(raw);
  const title = titleFrom(raw, raw);
  if (!css.trim()) return { title, html: body };
  return { title, html: `<div class="paste-root"><style>${scopeCssBlock(css)}</style>${body}</div>` };
}

/** Heuristic: does plain-text look like HTML source the user wants rendered? */
export function looksLikeHtml(s: string): boolean {
  return /^\s*</.test(s) && /<[a-z!][\s\S]*>/i.test(s);
}

function escapeText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Wrap non-HTML plain text so it renders with preserved whitespace. */
export function wrapText(text: string): string {
  return `<div style="white-space:pre-wrap;font-family:var(--font-sans,sans-serif);padding:12px;font-size:14px;line-height:1.5">${escapeText(text)}</div>`;
}

/** Derive a frame title from raw HTML (<title>, first heading) or text. */
export function titleFrom(rawHtml: string, fallbackText = ''): string {
  try {
    const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
    const t = doc.title?.trim();
    if (t) return truncate(t);
    const h = doc.querySelector('h1, h2, h3')?.textContent?.trim();
    if (h) return truncate(h);
    // Drop non-visible text sources so a <style>/<script> block can't become
    // the title (e.g. captured pages with inline CSS).
    doc.querySelectorAll('style, script, noscript').forEach((e) => e.remove());
    const body = doc.body?.textContent?.replace(/\s+/g, ' ').trim();
    if (body) return truncate(body);
    // Structural fallback for content with no visible text (images, wrappers).
    const first = doc.body?.firstElementChild;
    if (first) return truncate(`Captured <${first.tagName.toLowerCase()}>`);
  } catch {
    /* fall through */
  }
  const line = fallbackText.split('\n').find((l) => l.trim());
  return line ? truncate(line.trim()) : 'Pasted';
}

function truncate(s: string): string {
  return s.length > 40 ? s.slice(0, 40) + '…' : s;
}

export interface PasteContent {
  title: string;
  html: string;
}

/** Build frame content from clipboard data (sync sources: html, text). */
export function contentFromClipboard(html: string, text: string): PasteContent | null {
  if (html) return buildFromHtml(html);
  if (text) {
    if (looksLikeHtml(text)) return buildFromHtml(text);
    return { title: titleFrom('', text), html: wrapText(text) };
  }
  return null;
}

function createFrame(content: PasteContent): void {
  const store = useCanvasStore.getState();
  const { x, y } = screenToWorld(lastCursor, store.doc.viewport);
  const id = shortId();
  store.dispatch({
    t: 'frame.create',
    id,
    x: Math.round(x),
    y: Math.round(y),
    w: DEFAULT_W,
    title: content.title,
    html: content.html,
  });
  store.dispatch({ t: 'select', ids: [id] });
}

// ---- Cursor tracking (paste lands where the user last pointed) ----
let lastCursor = {
  x: typeof window !== 'undefined' ? window.innerWidth / 2 : 0,
  y: typeof window !== 'undefined' ? window.innerHeight / 2 : 0,
};
export function recordCursor(x: number, y: number): void {
  lastCursor = { x, y };
}

function attrEscape(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

/** Wrap trusted content in a sandboxed iframe. `allow-scripts` WITHOUT
 *  `allow-same-origin` → JS runs but cannot touch the parent app, cookies, or
 *  storage, so even "trusted" embeds stay contained. */
export function buildEmbedHtml(content: string): string {
  return `<iframe sandbox="allow-scripts" srcdoc="${attrEscape(content)}" style="width:100%;height:100%;border:0;display:block;background:#fff" title="live embed"></iframe>`;
}

/**
 * Paste clipboard content as a LIVE EMBED: a sandboxed iframe that runs
 * HTML+CSS+JS. Explicitly gated (⌘⇧V + confirm) because JS execution is the
 * trust line — never reachable from the normal paste path. Not composited into
 * the canvas/effects pipeline (it's an iframe).
 */
export async function pasteAsEmbed(): Promise<void> {
  if (isEditableTarget()) return;
  let text = '';
  try {
    text = await navigator.clipboard.readText();
  } catch {
    return; // clipboard permission denied / unavailable
  }
  if (!text.trim()) return;
  if (typeof window !== 'undefined' && !window.confirm('Paste as live embed? This runs the clipboard HTML/JS in a sandbox.')) {
    return;
  }
  const store = useCanvasStore.getState();
  const { x, y } = screenToWorld(lastCursor, store.doc.viewport);
  const id = shortId();
  store.beginTransaction('paste embed');
  try {
    store.dispatch({
      t: 'frame.create',
      id,
      x: Math.round(x),
      y: Math.round(y),
      w: 600,
      title: titleFrom(text, text) || 'Live embed',
      html: buildEmbedHtml(text),
    });
    store.dispatch({ t: 'frame.resize', id, w: 600, h: 400 }); // iframes need explicit height
    store.dispatch({ t: 'select', ids: [id] });
  } finally {
    store.endTransaction();
  }
}

function isEditableTarget(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
}

/** Window paste handler: turn clipboard HTML/text/image into a new frame. */
/** Allow image-file drops by preventing the browser's default (open the file). */
export function handleDragOver(e: DragEvent): void {
  const dt = e.dataTransfer;
  if (dt && Array.from(dt.items).some((it) => it.kind === 'file')) e.preventDefault();
}

/**
 * Drop one or more image files onto the canvas: each is ingested as a
 * content-hashed `asset://` ref (background-uploaded to the CDN) and placed as a
 * frame at the drop point. Non-image drops are ignored.
 */
export function handleDrop(e: DragEvent): void {
  const dt = e.dataTransfer;
  if (!dt) return;
  const files = Array.from(dt.files).filter((f) => f.type.startsWith('image/'));
  const jsonFiles = Array.from(dt.files).filter(
    (f) => f.type === 'application/json' || f.name.endsWith('.json'),
  );
  if (files.length === 0 && jsonFiles.length === 0) return;
  e.preventDefault();
  const world = screenToWorld({ x: e.clientX, y: e.clientY }, useCanvasStore.getState().doc.viewport);
  files.forEach((file, i) => {
    void ingestImage(file).then((ref) => {
      const alt = (file.name || 'image').replace(/"/g, '&quot;');
      useCanvasStore.getState().dispatch({
        t: 'frame.create',
        id: `frame_drop_${Date.now()}_${i}`,
        x: world.x + i * 24,
        y: world.y + i * 24,
        w: 400,
        title: file.name || 'Dropped image',
        html: `<img src="${ref}" alt="${alt}" style="display:block;max-width:100%;height:auto" />`,
      });
    });
  });
  jsonFiles.forEach((file, i) => {
    // jsdom's File.text() is unreliable; read bytes + decode (matches assetStore).
    void blobArrayBuffer(file).then((buf) => {
      let doc: unknown;
      try {
        doc = JSON.parse(new TextDecoder().decode(buf));
      } catch {
        return; // not JSON → ignore (like a non-image drop)
      }
      // Bodymovin sniff: v + fr + layers. Anything else isn't a Lottie drop.
      const d = doc as { v?: unknown; fr?: unknown; layers?: unknown };
      if (typeof d.v !== 'string' || typeof d.fr !== 'number' || !Array.isArray(d.layers)) return;
      if (validateLottie(doc).length > 0) return; // invalid → ignore silently
      void ingestLottieJson(doc).then((ref) => {
        const docW = (doc as { w?: unknown }).w;
        const w = typeof docW === 'number' ? Math.min(docW, 480) : 240;
        useCanvasStore.getState().dispatch({
          t: 'frame.create',
          id: `frame_lottie_drop_${Date.now()}_${i}`,
          x: world.x + i * 24,
          y: world.y + i * 24,
          w,
          title: file.name || 'Animation',
          html: `<div data-lottie="${ref}" style="width:${w}px;height:${w}px"></div>`,
        });
      });
    });
  });
}

export function handlePaste(e: ClipboardEvent): void {
  if (isEditableTarget()) return; // let inputs/textareas/CmdK paste normally
  const cd = e.clipboardData;
  if (!cd) return;

  const html = cd.getData('text/html');
  const text = cd.getData('text/plain');
  const imageItem = cd.items
    ? Array.from(cd.items).find((it) => it.type.startsWith('image/'))
    : undefined;

  if (!html && !text && !imageItem) return;
  e.preventDefault();

  const sync = contentFromClipboard(html, text);
  if (sync) {
    createFrame(sync);
    return;
  }

  if (imageItem) {
    const file = imageItem.getAsFile(); // must read synchronously
    if (!file) return;
    // Store as a content-hashed asset:// ref (background-uploads to the CDN)
    // rather than inlining base64 — keeps the doc small + cloud-resolvable.
    void ingestImage(file).then((ref) => {
      createFrame({
        title: 'Pasted image',
        html: `<img src="${ref}" style="display:block;max-width:100%;height:auto" alt="pasted" />`,
      });
    });
  }
}
